import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";

import { getInvoiceDetails } from "src/api/invoice";
import { useDownloadInvoiceMutation } from "src/api/mutations";
import { DataTable, type Column } from "src/components/common";
import { Button } from "src/components/ui/button";
import { useAuth } from "src/providers/AuthProvider";
import { getCustomizationValue, renderCurrency, renderNumber } from "src/lib/utils";
import type { InvoiceItem } from "src/types/invoice";

const itemColumns: Column<InvoiceItem>[] = [
	{ key: "item", header: "Item", primary: true, cell: (row) => row.product?.name ?? "—" },
	{ key: "qty", header: "Qty", className: "tw-w-[80px]", cell: (row) => renderNumber(row.qty) },
	{ key: "unit", header: "Unit price", cell: (row) => renderCurrency(row.product_price ?? 0) },
	{ key: "total", header: "Total", cell: (row) => renderCurrency(row.amount ?? 0) },
];

const Row = ({ label, value, strong }: { label: string; value: string; strong?: boolean }) => (
	<div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-py-1.5">
		<span className={strong ? "tw-text-[13.5px] tw-font-semibold tw-text-text" : "tw-text-[13px] tw-text-text-soft"}>{label}</span>
		<span className={strong ? "tw-text-[15px] tw-font-bold tw-text-text" : "tw-text-[13.5px] tw-text-text"}>{value}</span>
	</div>
);

const Field = ({ label, value }: { label: string; value?: string }) => (
	<div className="tw-rounded-[10px] tw-border tw-border-border tw-bg-bg tw-px-3 tw-py-2.5">
		<div className="tw-mb-1 tw-text-[11.5px] tw-font-medium tw-uppercase tw-tracking-wide tw-text-text-soft">{label}</div>
		<div className="tw-break-words tw-text-[13.5px] tw-text-text">{value || "—"}</div>
	</div>
);

interface PaymentDetailModalProps {
	/** The invoice uuid behind the payment — this reads the invoice, not the payment record. */
	paymentId: string;
}

const PaymentDetailModal = ({ paymentId }: PaymentDetailModalProps) => {
	const { user } = useAuth();

	const { data } = useQuery({
		queryKey: ["payments", paymentId],
		queryFn: () => getInvoiceDetails(paymentId),
		suspense: true,
	});

	const { mutate: downloadInvoice, isLoading: isDownloading } = useDownloadInvoiceMutation();

	const invoice = data?.data;

	const customerLabel = getCustomizationValue(user, "customer_label", true) || "Customer";
	const invoiceLabel = getCustomizationValue(user, "invoice_label", true) || "Invoice";

	return (
		<div className="tw-space-y-6">
			<div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
				<Field
					label={`${customerLabel} name`}
					value={[invoice?.customer?.first_name, invoice?.customer?.last_name].filter(Boolean).join(" ")}
				/>
				<Field label="Payment method" value="Transfer" />
				<Field label="Email" value={invoice?.customer?.email} />
				<Field label="Phone" value={invoice?.customer?.phone} />
			</div>

			<section>
				<h4 className="tw-mb-2.5 tw-text-[13px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-text-soft">Items</h4>
				<DataTable
					columns={itemColumns}
					data={invoice?.items ?? []}
					rowKey={(row, index) => row.id ?? index}
					rowsPerPageOptions={[10, 25, "All"]}
					emptyTitle="No items"
				/>
			</section>

			<section className="tw-rounded-[10px] tw-border tw-border-border tw-bg-bg tw-px-4 tw-py-3">
				<Row label="Subtotal" value={renderCurrency(invoice?.amount ?? 0)} />
				<Row label="Discount" value={renderCurrency(invoice?.discount_amount ?? 0)} />
				<Row label="VAT" value={renderCurrency(invoice?.vat ?? 0)} />
				<Row label={`${invoiceLabel} total`} value={renderCurrency(invoice?.total_amount ?? 0)} />
				<Row label="Transaction fee" value={renderCurrency(invoice?.transaction_fee ?? 0)} />
				<div className="tw-my-1.5 tw-h-px tw-bg-border" />
				<Row label="Transaction amount" value={renderCurrency(invoice?.transaction_amount ?? 0)} strong />
			</section>

			{invoice?.uuid && (
				<Button
					type="button"
					variant="secondary"
					className="tw-w-full tw-gap-1.5"
					disabled={isDownloading}
					onClick={() => downloadInvoice(invoice.uuid)}
				>
					<Download className="tw-size-4" />
					{isDownloading ? "Preparing..." : "Download receipt as PDF"}
				</Button>
			)}
		</div>
	);
};

export default PaymentDetailModal;
